import {
  all,
  call,
  delay,
  put,
  race,
  take,
  select,
  takeLatest,
} from 'redux-saga/effects';
import { compose } from 'redux';
import { apiCall } from 'app/dealer/actions';
import { generateApiUrl } from 'app/dealer/utils';
import { GET_BRANCHES_SUCCESS } from '../Branches/constants';
import { selectBranchesDictionary } from '../Branches/selectors';
import {
  setDealerIdBeingEdited,
  setInitialDealerProfileSettingsOptions,
  setSaveChangesModalIsVisible,
  setShowConfirmationAnimation,
  submitDealerProfileSettingsFail,
  submitDealerProfileSettingsSuccess,
} from './actions';
import {
  selectDealerIdBeingEdited,
  selectDealerProfileSettingsApplyChangesToAllSelected,
  selectNextSelectedDealerId,
} from './selectors';
import { selectBranchProfileSettingsDictionary } from './selectors';
import { getBranches } from '../Branches/actions';
import { DealerProfileSettingsActionTypes } from './constants';
import { DEALER_PROFILE_SETTINGS_TRACKING } from './tracking/index';
import { DealerProfileSettingsTracking } from './tracking/tracking';
import { selectNetworkId } from '../Network/selectors';

const {
  RESET_ALL_SUBMISSION_STATES,
  SET_SAVE_CHANGES_MODAL_IS_VISIBLE,
  SUBMIT_DEALER_PROFILE_SETTINGS_BEGIN,
} = DealerProfileSettingsActionTypes;

// TODO:
// improve docs here
// https://github.com/redux-saga/redux-saga/blob/master/packages/core/types/effects.d.ts

/**
 * -------------
 * @description
 * worker sagas
 * -------------
 */

/**
 * @description
 * dispatch action with user's branches dictionary
 * to setup the dealer profile settings in the store
 *
 * @generator
 * @returns {Generator<StrictEffect, void, Record<string, object>>}
 */
export function* handleResetSettingsData() {
  const branchesDictionary = yield select(selectBranchesDictionary);
  yield put(setInitialDealerProfileSettingsOptions({ branchesDictionary }));
}

/**
 * @description
 * shows the save changes confirmation animation
 *
 * if the user closes the modal, the saga completes
 * else we wait 10 seconds and close it automatically
 * @generator
 * @returns {Generator<StrictEffect, void, RaceEffect<TakeEffect|CallEffect>}
 */
export function* handleConfirmationFlow() {
  yield put(setShowConfirmationAnimation({ showConfirmationAnimation: true }));
  // if the user doesn't close modal in 3 seconds, close it automatically
  yield race({
    closeEvent: take(SET_SAVE_CHANGES_MODAL_IS_VISIBLE),
    timeout: delay(3000),
  });
  yield put(setShowConfirmationAnimation({ showConfirmationAnimation: false }));
}

/**
 * @description
 * selects current branch settings dictionary
 * selects apply to all branches state to see if user
 * wants to apply changes to all dealers
 *
 * executes PUT request to update dealer profile settings,
 * closes any modals that are open,
 * and refetches the new branches from backend (see TODO on this)
 *
 * @generator
 * @param {object}    param0
 * @param {{payload: {isAConfirmSaveChangesEvent: boolean}}} param0.isAConfirmSaveChangesEvent - true if submission happened from a confirm save changes modal
 * @returns {Generator<StrictEffect, void, Record<string, object>|boolean>}
 */
export function* handleSubmitDealerProfileSettingsAsync({ payload } = {}) {
  const { isAConfirmSaveChangesEvent } = payload || {};

  const branchProfileSettingsDictionary = yield select(
    selectBranchProfileSettingsDictionary,
  );
  const dealerProfileSettingsApplyChangesToAllSelected = yield select(
    selectDealerProfileSettingsApplyChangesToAllSelected,
  );
  const dealerIdBeingEdited = yield select(selectDealerIdBeingEdited);
  try {
    let dealerIds = [];
    if (dealerProfileSettingsApplyChangesToAllSelected) {
      dealerIds.push(...Object.keys(branchProfileSettingsDictionary));
    } else {
      dealerIds.push(dealerIdBeingEdited);
    }

    const body = JSON.stringify({
      dealerIds,
      dealerServices: branchProfileSettingsDictionary[dealerIdBeingEdited],
    });

    // yield for unit specs
    const url = yield generateApiUrl('/networks/me/dealers/services');
    const request = yield {
      method: 'PUT',
      body,
    };
    const response = yield compose(put, apiCall)(
      url,
      request,
      !process.env.REACT_APP_MOCK_SERVER,
      true, //Since this API call has been moved to COMMON API, passing this as true
    );

    // wait for promise to resolve/reject
    yield response;

    // dispatch success
    yield put(submitDealerProfileSettingsSuccess());

    yield call(DEALER_PROFILE_SETTINGS_TRACKING.trackDealerProfileSettings, {
      actionType: DealerProfileSettingsTracking.SUBMIT_SUCCESS,
      networkId: yield select(selectNetworkId),
    });

    // if submission happened from the save changes modal,
    // execute the confirmation event flow
    if (isAConfirmSaveChangesEvent) {
      yield call(handleConfirmationFlow);
    }
    yield put(setSaveChangesModalIsVisible({ modalIsVisible: false }));
    const nextSelectedDealerId = yield select(selectNextSelectedDealerId);
    yield put(setDealerIdBeingEdited({ dealerId: nextSelectedDealerId }));

    // fetch branches from backend
    // if the user has done a apply to all change
    if (dealerProfileSettingsApplyChangesToAllSelected) {
      yield put(getBranches());
    }
  } catch (error) {
    yield put(submitDealerProfileSettingsFail({ error }));
  }
}

/**
 * -------------
 * @description
 * watcher sagas
 * -------------
 */

export function* watchForEventsToInitializeSettingsData() {
  yield takeLatest(
    [RESET_ALL_SUBMISSION_STATES, GET_BRANCHES_SUCCESS],
    handleResetSettingsData,
  );
}

export function* watchSubmitDealerProfileSettingsBegin() {
  yield takeLatest(
    SUBMIT_DEALER_PROFILE_SETTINGS_BEGIN,
    handleSubmitDealerProfileSettingsAsync,
  );
}

/**
 * -------------
 * @description
 * root saga
 * -------------
 */
export function* dealerProfileSettingsSagas() {
  yield all([
    call(watchForEventsToInitializeSettingsData),
    call(watchSubmitDealerProfileSettingsBegin),
  ]);
}
